import type { InstrumentBrief, SkillLevel, StudentStatus } from '@/api/types'
import { SKILL_LEVEL_LABELS, STUDENT_STATUS_LABELS } from '@/lib/dictionaries'
import { Badge } from './Badge'

/** Plakietka instrumentu w jego własnym kolorze ze słownika. */
export function InstrumentBadge({ instrument }: { instrument: InstrumentBrief | null }) {
  if (!instrument) {
    return <span className="text-xs text-on-surface-variant">—</span>
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
      style={{
        // Kolor pochodzi z bazy, więc nie da się go wyrazić klasą Tailwinda.
        // 18 % krycia daje czytelne tło pod tekstem w pełnym nasyceniu.
        backgroundColor: `${instrument.color}1f`,
        color: instrument.color,
      }}
    >
      {instrument.name}
    </span>
  )
}

/** Ile plakietek pokazujemy, zanim reszta zamieni się w licznik. */
const WIDOCZNE_PLAKIETKI = 2

/**
 * Plakietki instrumentów — kilka pierwszych, reszta jako licznik.
 *
 * Uczeń bywa prowadzony na kilku instrumentach, a nauczyciel uczy nawet
 * kilkunastu. Komplet plakietek zamieniał się wtedy w kilkuwierszową ścianę,
 * która rozpychała komórkę tabeli i nagłówek karty. Ta sama zasada, co
 * w rozwijanym wyborze: dwie nazwy i licznik reszty.
 *
 * Pusta lista daje kreskę — tak samo jak brak instrumentu przy pojedynczej
 * plakietce.
 */
export function InstrumentBadges({
  instruments,
  limit = WIDOCZNE_PLAKIETKI,
}: {
  instruments: InstrumentBrief[]
  limit?: number
}) {
  if (instruments.length === 0) {
    return <span className="text-xs text-on-surface-variant">—</span>
  }

  const ukryte = instruments.slice(limit)

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {instruments.slice(0, limit).map((item) => (
        <InstrumentBadge key={item.id} instrument={item} />
      ))}
      {ukryte.length > 0 && (
        <span
          // Pełne nazwy w podpowiedzi: licznik mówi „ile”, a kto potrzebuje
          // „które”, dostaje je bez wchodzenia na kartę.
          title={ukryte.map((item) => item.name).join(', ')}
          className="inline-flex items-center whitespace-nowrap rounded-full bg-surface-container px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-on-surface-variant"
        >
          +{ukryte.length}
        </span>
      )}
    </span>
  )
}

const STATUS_TONES = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  ARCHIVED: 'neutral',
} as const

export function StudentStatusBadge({ status }: { status: StudentStatus }) {
  return <Badge tone={STATUS_TONES[status]}>{STUDENT_STATUS_LABELS[status]}</Badge>
}

export function SkillLevelBadge({ level }: { level: SkillLevel }) {
  return <Badge tone="neutral">{SKILL_LEVEL_LABELS[level]}</Badge>
}
